import { LBTrendIndicator, LBArrowUpIcon, LBArrowDownIcon } from '@linearb/buzz'

// Before→after direction chip for the backfill delta tiles: green up / red down.
// `positiveDirection='up'` for every metric, including lines removed — in a backfill,
// up always means data was recovered.
interface Props {
  before?: number | null
  after?: number | null
}

// `items-baseline` (not buzz's default `items-center`) so the chip's own baseline comes
// from its text rather than the icon — otherwise StatTile's baseline row lifts it ~2px
// above the "was N" figure. tailwind-merge lets the same override apply to LBTrendIndicator.
const CHIP = 'flex items-baseline gap-1 text-xs animate-in fade-in duration-300'

// For the two cases a percentage can't express. Mirrors LBTrendIndicator's own markup
// (which hardcodes `{value}%`) so the chips stay visually identical.
const TextChip: React.FC<{ up: boolean; label: string }> = ({ up, label }) => {
  const tone = up ? 'text-success' : 'text-destructive'
  const Arrow = up ? LBArrowUpIcon : LBArrowDownIcon
  return (
    <span className={`${CHIP} ${tone}`}>
      <Arrow className='size-3' />
      {label}
    </span>
  )
}

const DeltaIndicator: React.FC<Props> = ({ before, after }) => {
  if (before == null || after == null || after === before) {
    return null
  }

  const up = after > before

  // A zero baseline has no meaningful percentage, so say "new" instead of dividing by 0.
  if (before === 0) {
    return <TextChip up={up} label='new' />
  }

  // LBTrendIndicator renders the value verbatim, so round here.
  const pct = Math.round(Math.abs((after - before) / before) * 1000) / 10

  // A move under 0.05% would round to a self-contradicting "0%" next to an arrow.
  if (pct === 0) {
    return <TextChip up={up} label='<0.1%' />
  }

  return (
    <LBTrendIndicator
      value={pct}
      direction={up ? 'up' : 'down'}
      positiveDirection='up'
      className='items-baseline animate-in fade-in duration-300'
    />
  )
}

export default DeltaIndicator
